import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, Loader2, ClipboardCheck, PoundSterling, CalendarClock, ShieldCheck, AlertTriangle } from "lucide-react";
import { api, formatApiError as formatErr } from "../lib/api";

/* ────────────────────────────────────────────────────────────────────────
   Family Care Office — Care Arrangement (display name; technical
   identifiers/collection/routes remain "care_arrangements" / "arrangement")
   /workspace/family-circle/arrangement/:roomId

   Answers "what exactly did we agree?" — a companion to the free Care
   Discussion Room Agreement Checklist ("have we discussed/agreed this?"),
   not a replacement for it.
   ──────────────────────────────────────────────────────────────────────── */

export const SECTION_A_FIELDS = [
  { key: "care_recipient_label", label: "Person receiving care / client label", type: "text" },
  { key: "family_contact_name", label: "Family member / responsible family contact", type: "text" },
  { key: "carer_name", label: "Carer name", type: "text" },
  { key: "start_date", label: "Proposed / agreed start date", type: "text", placeholder: "e.g. 2026-10-06 or 'early October'" },
  { key: "care_type", label: "Type of care arrangement", type: "text", placeholder: "e.g. Live-in, hourly, waking night" },
  { key: "expected_duration", label: "Expected duration (where known)", type: "text", placeholder: "e.g. Ongoing, 3-6 months" },
  { key: "working_pattern", label: "Working pattern / rota", type: "textarea" },
  { key: "expected_hours", label: "Expected hours", type: "text", placeholder: "e.g. 40 hours/week" },
  { key: "night_arrangement", label: "Night-time arrangement (where applicable)", type: "textarea" },
  { key: "breaks_rest_arrangement", label: "Break / rest arrangements", type: "textarea" },
  { key: "main_responsibilities", label: "Main agreed responsibilities / support", type: "textarea" },
  { key: "responsibilities_not_included", label: "Tasks specifically agreed as NOT included (if any)", type: "textarea" },
];

export const SECTION_B_FIELDS = [
  { key: "agreed_rate", label: "Agreed rate", type: "text", placeholder: "e.g. £18" },
  { key: "rate_type", label: "Hourly / daily / other", type: "text", placeholder: "e.g. Hourly" },
  { key: "rate_covers", label: "What the agreed rate covers", type: "textarea" },
  { key: "payment_frequency", label: "Payment frequency", type: "text", placeholder: "e.g. Weekly, every Friday" },
  { key: "payment_method", label: "Payment method", type: "text", placeholder: "e.g. Bank transfer" },
  { key: "responsible_payer", label: "Responsible payer", type: "text" },
  { key: "expenses_arrangement", label: "Expenses arrangements", type: "textarea" },
  { key: "mileage_travel_arrangement", label: "Mileage / travel arrangements (where applicable)", type: "textarea" },
  { key: "food_allowance_arrangement", label: "Food allowance / arrangements (where applicable)", type: "textarea" },
  { key: "deposit_arrangement", label: "Deposit arrangements (where applicable)", type: "textarea" },
  { key: "cancellation_arrangement", label: "Cancellation arrangements", type: "textarea" },
  { key: "notice_period", label: "Notice period", type: "text" },
  { key: "other_terms", label: "Any other agreed financial / practical term", type: "textarea" },
];

function Field({ field, value, onChange, prefilled }) {
  const common = {
    id: `af-${field.key}`,
    value: value || "",
    onChange: (e) => onChange(field.key, e.target.value),
    placeholder: field.placeholder || "",
    className: "mt-1.5 w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-purple/30 focus:border-purple/40",
    "data-testid": `arrangement-field-${field.key}`,
  };
  return (
    <div>
      <label htmlFor={common.id} className="flex items-center gap-2 text-sm font-medium text-ink">
        {field.label}
        {prefilled && (
          <span className="pill bg-purple/10 text-purple text-[11px]" data-testid={`arrangement-prefilled-${field.key}`}>
            Carried forward
          </span>
        )}
      </label>
      {field.type === "textarea" ? (
        <textarea rows={2} {...common} />
      ) : (
        <input type="text" {...common} />
      )}
    </div>
  );
}

function SectionCard({ eyebrow, title, description, icon: Icon, fields, values, prefilledSet, onChange, onSave, saving, savedAt }) {
  return (
    <section className="rounded-3xl border border-slate-200 bg-white p-6 md:p-8" data-testid={`arrangement-section-${eyebrow.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="flex items-start gap-3 mb-1">
        <div className="w-10 h-10 rounded-xl bg-purple/10 text-purple flex items-center justify-center shrink-0" aria-hidden="true">
          <Icon className="w-5 h-5" strokeWidth={1.75} />
        </div>
        <div>
          <div className="overline text-purple">{eyebrow}</div>
          <h2 className="font-display text-xl font-semibold">{title}</h2>
        </div>
      </div>
      <p className="text-sm text-ink-muted mt-2 mb-6 max-w-2xl">{description}</p>
      <div className="grid sm:grid-cols-2 gap-5">
        {fields.map((f) => (
          <Field key={f.key} field={f} value={values[f.key]} prefilled={prefilledSet.has(f.key)} onChange={onChange} />
        ))}
      </div>
      <div className="mt-6 pt-5 border-t border-slate-100 flex items-center gap-3">
        <button
          type="button"
          onClick={onSave}
          disabled={saving}
          className="btn-purple text-sm"
          data-testid={`arrangement-save-${eyebrow.replace(/\s+/g, "-").toLowerCase()}`}
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
          Save this section
        </button>
        {savedAt && (
          <span className="text-xs text-teal" data-testid={`arrangement-saved-${eyebrow.replace(/\s+/g, "-").toLowerCase()}`}>
            Saved · last saved {savedAt}
          </span>
        )}
      </div>
    </section>
  );
}

export default function ArrangementRecord() {
  const { roomId } = useParams();
  const [loading, setLoading] = useState(true);
  const [room, setRoom] = useState(null);
  const [arrangement, setArrangement] = useState(null);
  const [prefilledSet, setPrefilledSet] = useState(new Set());
  const [values, setValues] = useState({});
  const [savingA, setSavingA] = useState(false);
  const [savingB, setSavingB] = useState(false);
  const [savingC, setSavingC] = useState(false);
  const [savedAtA, setSavedAtA] = useState(null);
  const [savedAtB, setSavedAtB] = useState(null);
  const [savedAtC, setSavedAtC] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const roomRes = await api.get(`/api/care-rooms/${roomId}`);
        if (cancelled) return;
        setRoom(roomRes.data.room);

        let record = null;
        try {
          const arrRes = await api.get(`/api/care-arrangements/by-room/${roomId}`);
          record = arrRes.data;
        } catch (err) {
          if (err?.response?.status !== 404) throw err;
          // No Care Arrangement yet — create it now, in the same step, so
          // the family never sees a separate "start" confirmation page.
          const createRes = await api.post("/api/care-arrangements", { room_id: roomId });
          record = createRes.data.arrangement;
        }
        if (cancelled) return;
        setArrangement(record);
        setValues(record);
        setPrefilledSet(new Set(record.prefilled_fields || []));
      } catch (err) {
        if (!cancelled) setError(formatErr(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [roomId]);

  const onChange = (key, val) => setValues((v) => ({ ...v, [key]: val }));

  const saveSection = async (fieldKeys, setSaving, setSavedAt) => {
    setSaving(true);
    try {
      const payload = {};
      fieldKeys.forEach((k) => { payload[k] = values[k] ?? null; });
      const { data } = await api.put(`/api/care-arrangements/${arrangement.arrangement_id}`, payload);
      setArrangement(data);
      setValues((v) => ({ ...v, ...data }));
      setSavedAt(new Date().toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }));
      toast.success("Saved");
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="container-jcc py-24 text-center text-ink-muted" data-testid="arrangement-loading">
        <Loader2 className="w-5 h-5 animate-spin inline-block mr-2" /> Loading…
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-jcc py-24 max-w-lg text-center" data-testid="arrangement-error">
        <p className="text-rose-700">{error}</p>
        <Link to="/care-rooms" className="btn-outline mt-6 inline-flex">
          <ArrowLeft className="w-4 h-4" /> Back to My rooms
        </Link>
      </div>
    );
  }

  return (
    <div className="bg-[#fafaf7] min-h-screen" data-testid="page-arrangement-record">
      <div className="container-jcc py-10 md:py-14 max-w-4xl">
        <div className="mb-6">
          <Link to={`/care-discussion-room/${roomId}`} className="btn-outline !px-4 !py-2 text-sm" data-testid="arrangement-back-to-room">
            <ArrowLeft className="w-4 h-4" /> Back to Care Partnership Room
          </Link>
        </div>

        <div className="mb-8">
          <div className="overline text-purple mb-3">Family Care Office</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold leading-tight">
            Your Care Arrangement
          </h1>
          <p className="mt-3 text-lg text-ink font-medium">
            Let&rsquo;s get everything organised.
          </p>
          <p className="mt-2 text-ink-muted leading-relaxed max-w-2xl">
            We&rsquo;ve filled in what we already know. Check the details, add what&rsquo;s missing
            and update anything that&rsquo;s changed.
          </p>
          {room?.client_label && (
            <p className="mt-2 text-sm text-ink-soft">
              Care Partnership Room: <strong>{room.client_label}</strong>
            </p>
          )}
        </div>

        <div className="space-y-6">
          <SectionCard
            eyebrow="Section A"
            title="The arrangement"
            description="What you've agreed about care itself — who, when and how."
            icon={ClipboardCheck}
            fields={SECTION_A_FIELDS}
            values={values}
            prefilledSet={prefilledSet}
            onChange={onChange}
            onSave={() => saveSection(SECTION_A_FIELDS.map((f) => f.key), setSavingA, setSavedAtA)}
            saving={savingA}
            savedAt={savedAtA}
          />
          <SectionCard
            eyebrow="Section B"
            title="Money & practical working terms"
            description="What you've agreed about payment and practical terms. This is a record of your agreement — not tax, employment-status or legal advice."
            icon={PoundSterling}
            fields={SECTION_B_FIELDS}
            values={values}
            prefilledSet={prefilledSet}
            onChange={onChange}
            onSave={() => saveSection(SECTION_B_FIELDS.map((f) => f.key), setSavingB, setSavedAtB)}
            saving={savingB}
            savedAt={savedAtB}
          />

          <section className="rounded-3xl border border-slate-200 bg-white p-6 md:p-8" data-testid="arrangement-section-review">
            <div className="flex items-start gap-3 mb-1">
              <div className="w-10 h-10 rounded-xl bg-purple/10 text-purple flex items-center justify-center shrink-0" aria-hidden="true">
                <CalendarClock className="w-5 h-5" strokeWidth={1.75} />
              </div>
              <div>
                <div className="overline text-purple">Review</div>
                <h2 className="font-display text-xl font-semibold">Next Care Arrangement review date</h2>
              </div>
            </div>
            <p className="text-sm text-ink-muted mt-2 mb-6 max-w-2xl">
              Optional. If you set a date, we&rsquo;ll send you and your carer a simple reminder beforehand —
              use your Room to discuss any changes and re-confirm the Arrangement.
            </p>
            <div className="max-w-xs">
              <label htmlFor="af-next_review_date" className="text-sm font-medium text-ink">Next review date</label>
              <input
                id="af-next_review_date"
                type="date"
                value={values.next_review_date || ""}
                onChange={(e) => onChange("next_review_date", e.target.value)}
                data-testid="arrangement-field-next_review_date"
                className="mt-1.5 w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-purple/30 focus:border-purple/40"
              />
            </div>
            <div className="mt-6 pt-5 border-t border-slate-100 flex items-center gap-3">
              <button
                type="button"
                onClick={() => saveSection(["next_review_date"], setSavingC, setSavedAtC)}
                disabled={savingC}
                className="btn-purple text-sm"
                data-testid="arrangement-save-review"
              >
                {savingC ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                Save review date
              </button>
              {savedAtC && (
                <span className="text-xs text-teal" data-testid="arrangement-saved-review">Saved · last saved {savedAtC}</span>
              )}
            </div>

            <div className="mt-6 pt-5 border-t border-slate-100" data-testid="arrangement-carer-ack-status">
              {arrangement?.acknowledged_by_carer_at ? (
                arrangement.acknowledged_version === arrangement.version ? (
                  <p className="text-sm text-teal flex items-start gap-2">
                    <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>
                      Your carer acknowledged this Arrangement on{" "}
                      {new Date(arrangement.acknowledged_by_carer_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}.
                    </span>
                  </p>
                ) : (
                  <p className="text-sm text-amber-700 flex items-start gap-2" data-testid="arrangement-ack-stale-notice">
                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>
                      Your carer&rsquo;s acknowledgement (from{" "}
                      {new Date(arrangement.acknowledged_by_carer_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })})
                      {" "}predates your latest update — they&rsquo;ll be asked to acknowledge it again.
                    </span>
                  </p>
                )
              ) : (
                <p className="text-sm text-ink-soft">Your carer has not yet acknowledged this Arrangement.</p>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
